import { animated, useSprings } from "@react-spring/web";
import { useEffect, useMemo } from "react";

import hatch from "../assets/hatch.png";

const COUNT = 100;
const MAX_DELAY_MS = 800;
const TOTAL_MS = MAX_DELAY_MS + 3500; // delay + fall + bounce + exit

const easeIn = (t: number) => t * t;
const easeOut = (t: number) => t * (2 - t);

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

interface HatcheryRainProps {
  onComplete: () => void;
}

export const HatcheryRain: React.FC<HatcheryRainProps> = ({ onComplete }) => {
  const eggs = useMemo(
    () =>
      Array.from({ length: COUNT }, (_, i) => {
        const size = rand(44, 76);
        const landingY = window.innerHeight - size - 10;
        return {
          id: i,
          left: rand(0, 100),
          spin: rand(-540, 540),
          delay: rand(0, MAX_DELAY_MS),
          size,
          landingY,
          bounceY: landingY - rand(200, 420), // bounce up high
          exitY: window.innerHeight + 200,
          fallDuration: rand(700, 1100),
          bounceDuration: rand(280, 450),
          exitDuration: rand(450, 750),
        };
      }),
    [],
  );

  const springs = useSprings(
    COUNT,
    eggs.map((egg) => ({
      from: { y: -80, rotate: 0 },
      to: async (next: (props: object) => Promise<void>) => {
        await next({ y: egg.landingY, rotate: egg.spin * 0.5, config: { duration: egg.fallDuration, easing: easeIn } });
        await next({
          y: egg.bounceY,
          rotate: egg.spin * 0.75,
          config: { duration: egg.bounceDuration, easing: easeOut },
        });
        await next({ y: egg.exitY, rotate: egg.spin, config: { duration: egg.exitDuration, easing: easeIn } });
      },
      delay: egg.delay,
    })),
  );

  useEffect(() => {
    const t = setTimeout(onComplete, TOTAL_MS);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
      {springs.map((spring, i) => (
        <animated.img
          key={eggs[i].id}
          src={hatch}
          alt=""
          style={{
            position: "absolute",
            top: 0,
            left: `${eggs[i].left}vw`,
            width: eggs[i].size,
            height: eggs[i].size,
            objectFit: "contain",
            ...spring,
          }}
        />
      ))}
    </div>
  );
};
